import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, inr } from "../../lib/api";
import { ShieldCheck, CheckCircle2, Lock, AlertTriangle } from "lucide-react";
import { useBrandLogo, logoUrlFor } from "../../lib/logo";

export default function PayLink() {
  const { orderId } = useParams();
  const [state, setState] = useState({ loading: true });
  const [placing, setPlacing] = useState(false);
  const [paid, setPaid] = useState(false);
  const { logoUrl, version } = useBrandLogo();

  useEffect(() => {
    api.get(`/pay/order/${orderId}`)
      .then(r => setState({ loading: false, order: r.data }))
      .catch(e => setState({ loading: false, error: e.response?.data?.detail || "Order not found" }));
  }, [orderId]);

  const pay = async () => {
    if (typeof window.Razorpay === "undefined") {
      alert("Razorpay is loading, please retry in a moment.");
      return;
    }
    setPlacing(true);
    try {
      const cr = await api.post(`/pay/order/${orderId}/razorpay-order`);
      const options = {
        key: cr.data.key_id,
        amount: cr.data.amount, currency: cr.data.currency,
        order_id: cr.data.razorpay_order_id,
        name: "DailyBeans",
        description: `Order ${cr.data.order_no} · ${inr(cr.data.total)}`,
        image: `${window.location.origin}${logoUrlFor(version)}`.replace(window.location.origin, process.env.REACT_APP_BACKEND_URL || ""),
        prefill: { email: cr.data.email || "", contact: cr.data.phone || "" },
        theme: { color: "#3B2F2F" },
        handler: async (res) => {
          try {
            await api.post(`/pay/order/${orderId}/verify`, {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            setPaid(true);
          } catch (err) {
            alert(err.response?.data?.detail || "Payment verification failed");
            setPlacing(false);
          }
        },
        modal: { ondismiss: () => setPlacing(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        alert(`Payment failed: ${resp.error?.description || "Unknown error"}`);
        setPlacing(false);
      });
      rzp.open();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to initiate payment");
      setPlacing(false);
    }
  };

  if (state.loading) {
    return <PayShell><div className="text-stone-500 text-sm" data-testid="pay-loading">Loading order…</div></PayShell>;
  }

  if (state.error) {
    return (
      <PayShell>
        <div className="text-center py-8" data-testid="pay-error">
          <AlertTriangle className="w-10 h-10 mx-auto text-[#B00020] mb-3"/>
          <div className="font-outfit font-semibold text-lg">Order not found</div>
          <div className="text-sm text-stone-500 mt-1">{state.error}</div>
        </div>
      </PayShell>
    );
  }

  const o = state.order;

  if (paid || o.already_paid) {
    return (
      <PayShell>
        <div className="text-center py-6" data-testid="pay-already-paid">
          <CheckCircle2 className="w-14 h-14 mx-auto text-[#2E4A35] mb-3"/>
          <div className="font-serif-display text-3xl text-[#3B2F2F]">Payment received</div>
          <div className="text-sm text-stone-500 mt-2">Order <span className="font-mono">{o.order_no}</span></div>
          <div className="text-xs text-stone-500 mt-4">You can close this page — we'll send a confirmation SMS shortly.</div>
        </div>
      </PayShell>
    );
  }

  return (
    <PayShell>
      <div className="text-xs uppercase tracking-widest text-[#D4AF37] mb-1">Pay online</div>
      <h1 className="font-serif-display text-3xl text-[#3B2F2F] mb-1" data-testid="pay-order-no">Order {o.order_no}</h1>
      <div className="text-sm text-stone-500 mb-6">for {o.email || "your recent order"}{o.city ? ` · ${o.city}` : ""}</div>

      <div className="bg-[#FDFBF7] border border-stone-200 rounded-lg p-5 mb-5">
        <div className="flex justify-between text-sm text-stone-600"><span>Items</span><span>{o.item_count}</span></div>
        <div className="flex justify-between text-sm text-stone-600 mt-1"><span>Currently</span>
          <span className="uppercase text-[11px] bg-stone-200 rounded-full px-2 py-0.5">{o.payment_method}</span>
        </div>
        <div className="border-t border-stone-200 mt-4 pt-4 flex justify-between items-baseline">
          <span className="font-outfit font-semibold text-lg">Amount to pay</span>
          <span className="font-serif-display text-3xl text-[#3B2F2F]" data-testid="pay-amount">{inr(o.total)}</span>
        </div>
      </div>

      <button onClick={pay} disabled={placing} data-testid="pay-btn"
              className="w-full bg-[#3B2F2F] text-white rounded-full py-4 font-outfit font-semibold hover:bg-black transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
        <Lock className="w-4 h-4"/>
        {placing ? "Opening Razorpay…" : `Pay ${inr(o.total)} with Razorpay`}
      </button>

      <div className="mt-4 flex items-center justify-center gap-4 text-[11px] text-stone-500">
        <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3"/> 256-bit SSL</span>
        <span>·</span>
        <span>UPI · Cards · Netbanking · Wallets</span>
      </div>
      <div className="mt-3 text-center text-[11px] text-stone-400">
        Payment goes directly to DailyBeans Foods & Beverages Pvt Ltd via Razorpay. This link works only for this specific order.
      </div>
    </PayShell>
  );
}

function PayShell({ children }) {
  const { logoUrl } = useBrandLogo();
  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      <div className="border-b border-stone-200 bg-white">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
          <img src={logoUrl} alt="DailyBeans" className="w-10 h-10 object-contain"/>
          <div>
            <div className="font-serif-display text-2xl text-[#3B2F2F]">DailyBeans</div>
            <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Secure Payment</div>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-lg bg-white border border-stone-200 rounded-2xl shadow-sm p-6 md:p-8">
          {children}
        </div>
      </div>
      <div className="text-center text-[11px] text-stone-400 py-4 border-t border-stone-100">
        DailyBeans Foods & Beverages Pvt Ltd
      </div>
    </div>
  );
}
